import React, { useState, useEffect } from "react";
import {
  Flex,
  Stack,
} from "@chakra-ui/react";
import { useLazyQuery } from "@apollo/client";
import DataTable from "./DataTable";
import DataTableV2 from "../ui/data-table";
import usePaginatedFetch from "./usePaginatedFetch";
import PageTitle from "../PageTitle";
import { FLAGS, useBoolFlag } from "../../services/featureFlags";

const PaginationTable = ({
  title,
  columns,
  filters,
  paginationState,
  filterInputs,
  query,
  onClickRow,
  queryName,
  itemKey,
  tableId,
}) => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [fetchData, { loading, data: queryData }] = useLazyQuery(query, { fetchPolicy: "no-cache" });
  const [pagination, setPagination, savePagination] = paginationState;

  // Only tables that opt in with a `tableId` can be switched to the new
  // table; everything else stays on the Chakra one.
  const responsiveTables = useBoolFlag(FLAGS.WEB_RESPONSIVE_TABLES);
  const useNewTable = Boolean(tableId) && responsiveTables;

  // The legacy table runs the query itself; the new one is presentational, so
  // on that path this wrapper owns the fetch instead. Only ever one of the two.
  usePaginatedFetch({
    fetchData: useNewTable ? fetchData : null,
    pagination,
    filters,
    savePagination,
  });

  useEffect(() => {
    if (queryData) {
      setData(queryData[queryName][itemKey] || []);
      setTotalRecords(queryData[queryName].total);
      if (queryData[queryName].total === 0) {
        setPagination((prev) => {
          if (prev.pageIndex > 0) {
            savePagination({ pageIndex: 0, pageSize: prev.pageSize });
            return { ...prev, pageIndex: 0 };
          }
          return prev;
        });
      }
    }
  }, [queryData, queryName, itemKey, setPagination, savePagination]);

  return (
    <Stack minW="0">
      <PageTitle title={title} />
      {filterInputs && (filterInputs)}

      {useNewTable ? (
        // minW="0" lets the table shrink inside the flex row so it can scroll
        // horizontally instead of forcing the page wide.
        <Flex w="full" minW="0" pb="40px">
          <DataTableV2
            tableId={tableId}
            columns={columns}
            data={data}
            onClickRow={onClickRow}
            pagination={pagination}
            onPaginationChange={setPagination}
            rowCount={totalRecords}
            loading={loading}
          />
        </Flex>
      ) : (
        <Flex w="full" pb="40px">
          <DataTable
            loading={loading}
            columns={columns}
            data={data}
            totalRecords={totalRecords}
            filters={filters}
            paginationState={paginationState}
            fetchData={fetchData}
            onClickRow={onClickRow}
          />
        </Flex>
      )}
    </Stack>
  );
};

export default PaginationTable;
